import { Link } from 'react-router';

import { useWorkspaceTimeZone } from '@/features/workspace/useWorkspace';
import { Icon } from './Icon';

export interface ZoneNoteProps {
  /** Where "Change" goes. Omit it when this person cannot change the workspace's zone. */
  readonly changeTo?: string;
}

/** "Central Time" for America/Chicago; the zone's own name when the platform has none. */
function friendlyZoneName(zone: string): string {
  try {
    const name = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longGeneric' })
      .formatToParts(0)
      .find((part) => part.type === 'timeZoneName');
    return name?.value ?? zone;
  } catch {
    return zone;
  }
}

/**
 * States which time zone a form's times are in, instead of asking for it.
 *
 * The workspace has one zone, set once in Settings, and every appointment in it
 * is that zone's wall-clock time. A form that makes someone pick a zone is
 * asking for something the system already knows, and offers them a way to book
 * a visit for the wrong hour. This shows the zone as a plain fact, read-only,
 * with a "Change" link for the rare person who needs to go and alter the setting.
 *
 * Needs a router and the signed-in session, like the pages it sits in.
 *
 * @param props see {@link ZoneNoteProps}
 * @returns the note
 */
export function ZoneNote({ changeTo }: ZoneNoteProps): React.JSX.Element {
  const zone = useWorkspaceTimeZone();

  return (
    <p className="fo-zone-note">
      <Icon name="clock" className="fo-zone-note__icon" />
      <span>
        Times are in {friendlyZoneName(zone)} ({zone}).
      </span>
      {changeTo === undefined ? null : <Link to={changeTo}>Change</Link>}
    </p>
  );
}
